import React from 'react';

const IRREGULAR_VERBS: Record<string, string[]> = {
  make: ['makes', 'making', 'made'],
  take: ['takes', 'taking', 'took', 'taken'],
  get: ['gets', 'getting', 'got', 'gotten'],
  go: ['goes', 'going', 'went', 'gone'],
  come: ['comes', 'coming', 'came'],
  run: ['runs', 'running', 'ran'],
  see: ['sees', 'seeing', 'saw', 'seen'],
  say: ['says', 'saying', 'said'],
  know: ['knows', 'knowing', 'knew', 'known'],
  give: ['gives', 'giving', 'gave', 'given'],
  find: ['finds', 'finding', 'found'],
  think: ['thinks', 'thinking', 'thought'],
  tell: ['tells', 'telling', 'told'],
  become: ['becomes', 'becoming', 'became'],
  leave: ['leaves', 'leaving', 'left'],
  feel: ['feels', 'feeling', 'felt'],
  bring: ['brings', 'bringing', 'brought'],
  begin: ['begins', 'beginning', 'began', 'begun'],
  keep: ['keeps', 'keeping', 'kept'],
  hold: ['holds', 'holding', 'held'],
  write: ['writes', 'writing', 'wrote', 'written'],
  stand: ['stands', 'standing', 'stood'],
  hear: ['hears', 'hearing', 'heard'],
  let: ['lets', 'letting'],
  mean: ['means', 'meaning', 'meant'],
  set: ['sets', 'setting'],
  meet: ['meets', 'meeting', 'met'],
  pay: ['pays', 'paying', 'paid'],
  sit: ['sits', 'sitting', 'sat'],
  speak: ['speaks', 'speaking', 'spoke', 'spoken'],
  lie: ['lies', 'lying', 'lay', 'lain'],
  lead: ['leads', 'leading', 'led'],
  read: ['reads', 'reading'],
  grow: ['grows', 'growing', 'grew', 'grown'],
  lose: ['loses', 'losing', 'lost'],
  fall: ['falls', 'falling', 'fell', 'fallen'],
  send: ['sends', 'sending', 'sent'],
  build: ['builds', 'building', 'built'],
  understand: ['understands', 'understanding', 'understood'],
  draw: ['draws', 'drawing', 'drew', 'drawn'],
  break: ['breaks', 'breaking', 'broke', 'broken'],
  spend: ['spends', 'spending', 'spent'],
  cut: ['cuts', 'cutting'],
  rise: ['rises', 'rising', 'rose', 'risen'],
  drive: ['drives', 'driving', 'drove', 'driven'],
  buy: ['buys', 'buying', 'bought'],
  wear: ['wears', 'wearing', 'wore', 'worn'],
  choose: ['chooses', 'choosing', 'chose', 'chosen'],
};

export function buildHighlightRegex(targetWord: string): RegExp | null {
  if (!targetWord) return null;
  // Очищаем ударения и комбинируемые диакритические знаки: "máke óut" -> "make out"
  const clean = targetWord
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  if (!clean) return null;

  const patterns = new Set<string>();
  const words = clean.split(/\s+/);

  if (words.length > 1) {
    // Phrasal verb or idiom: "make out", "let in", "keep one's chin up"
    const firstWord = words[0];
    const restParts = words.slice(1).map(w => {
      const cleanW = w.replace(/[’']/g, "'").toLowerCase();
      if (cleanW === "one's" || cleanW === "ones") {
        return "(?:one['’]s|my|your|his|her|its|our|their|someone['’]s|[a-z]+['’]s|the)";
      }
      if (cleanW === "someone" || cleanW === "somebody") {
        return "(?:someone|somebody|anyone|anybody|everyone|everybody|me|you|him|her|us|them|[a-z]+)";
      }
      if (cleanW === "something") {
        return "(?:something|anything|everything|it|[a-z]+)";
      }
      return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    });
    const rest = restParts.join('\\s+');

    const firstForms = [firstWord];
    if (IRREGULAR_VERBS[firstWord]) {
      firstForms.push(...IRREGULAR_VERBS[firstWord]);
    } else {
      if (firstWord.endsWith('e')) {
        firstForms.push(firstWord.slice(0, -1) + '[a-z]*');
      } else if (firstWord.endsWith('y')) {
        firstForms.push(firstWord.slice(0, -1) + '[a-z]*');
      } else {
        firstForms.push(firstWord + '[a-z]*');
      }
    }

    const firstPattern = `(?:${firstForms.join('|')})`;
    // Слитное использование: "making out"
    patterns.add(`\\b${firstPattern}\\s+${rest}\\b`);
    // Разделяемое использование (до 3 слов между глаголом и частицей, e.g. "let me in", "turn the light on"):
    patterns.add(`\\b${firstPattern}\\b(?=(?:\\s+\\w+){1,3}\\s+${rest}\\b)`);
    patterns.add(`(?<=\\b${firstPattern}\\b(?:\\s+\\w+){1,3}\\s+)${rest}\\b`);
  } else {
    // Одиночное слово: "mark", "worry", "make", "bad-tempered"
    const escaped = clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const forms = [escaped];

    if (IRREGULAR_VERBS[clean]) {
      forms.push(...IRREGULAR_VERBS[clean]);
    } else {
      if (clean.endsWith('e')) {
        forms.push(clean.slice(0, -1) + '[a-z]*');
      } else if (clean.endsWith('y')) {
        forms.push(clean.slice(0, -1) + '[a-z]*');
      } else if (!clean.includes('-')) {
        forms.push(escaped + '[a-z]*');
      }
    }
    patterns.add(`\\b(?:${forms.join('|')})\\b`);
  }

  try {
    return new RegExp(`(${Array.from(patterns).join('|')})`, 'gi');
  } catch {
    return new RegExp(`(${clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  }
}

export function renderHighlightedSentence(
  sentence: string,
  wordOriginal: string,
  style?: React.CSSProperties
): React.ReactNode {
  if (!sentence) return null;
  const regex = buildHighlightRegex(wordOriginal);
  if (!regex) return <span>{sentence}</span>;

  const parts = sentence.split(regex);
  const defaultHighlightStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#c084fc',
    letterSpacing: '0.01em',
    ...style,
  };

  return (
    <span>
      {parts.map((part, i) => {
        regex.lastIndex = 0;
        const isMatch = regex.test(part);
        if (isMatch) {
          return (
            <span key={i} style={defaultHighlightStyle}>
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

export function renderBlankedSentence(
  sentence: string,
  wordOriginal: string
): React.ReactNode {
  if (!sentence) return null;

  const blankStyle: React.CSSProperties = {
    color: '#F59E0B',
    fontWeight: 700,
    letterSpacing: '4px',
    margin: '0 3px',
    textShadow: '0 0 10px rgba(245, 158, 11, 0.25)',
  };

  // Если в предложении уже есть прочерк из бэкенда
  if (/_{3,}/.test(sentence)) {
    const parts = sentence.split(/_{3,}/);
    return (
      <span>
        {parts.map((part, i) => (
          <span key={i}>
            {part}
            {i < parts.length - 1 && (
              <span style={blankStyle}>
                ______
              </span>
            )}
          </span>
        ))}
      </span>
    );
  }

  // Если прочерка не было (например, слово стояло в другой грамматической форме),
  // находим форму слова через умный регекс и скрываем её
  const regex = buildHighlightRegex(wordOriginal);
  if (!regex) return <span>{sentence}</span>;

  const parts = sentence.split(regex);
  return (
    <span>
      {parts.map((part, i) => {
        regex.lastIndex = 0;
        const isMatch = regex.test(part);
        if (isMatch) {
          return (
            <span key={i} style={blankStyle}>
              ______
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}
